import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";

const navItems = [
  { name: "Home", path: "/" },
  { name: "About", path: "/about" },
  { name: "Dignitaries", path: "/dignitaries" },
  { name: "Committee", path: "/committee" },
  {
    name: "Call",
    dropdown: [
      { name: "Call for Abstract", path: "/call/abstract" },
      { name: "Call for Full Papers", path: "/call/papers" },
      { name: "Call for Oral Presentations", path: "/call/oral" },
      { name: "Call for Poster Presentations", path: "/call/poster" },
    ],
  },
  { name: "Schedule", path: "/schedule" },
  { name: "Registration", path: "/registration" },
  { name: "Sponsorship", path: "/sponsorship" },
  {
    name: "Speakers",
    dropdown: [
      { name: "Keynote Speakers", path: "/speakers/keynote" },
      { name: "Invited Speakers", path: "/speakers/invited" },
      { name: "Session Chairs", path: "/speakers/chairs" },
    ],
  },
  { name: "Accommodation", path: "/accommodation" },
  { name: "Contact us", path: "/contact" },
  { name: "Gallery", path: "/gallery" },
];

export default function Navbar() {
  const [isSticky, setIsSticky] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const onScroll = () => setIsSticky(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const active = (path) =>
    path === "/"
      ? location.pathname === path
      : location.pathname.startsWith(path);
  const itemActive = (item) =>
    item.dropdown
      ? item.dropdown.some((child) => active(child.path))
      : active(item.path);
  const classes = (selected) =>
    `rounded px-2 py-2 text-[12px] font-medium transition-colors 2xl:px-3 2xl:text-sm ${selected ? "bg-gray-800 text-white" : "text-gray-700 hover:bg-blue-100 hover:text-primary-600"}`;

  return (
    <nav
      className={`fixed top-0 z-50 w-full bg-white ${isSticky ? "shadow-xl" : "shadow-md"}`}
    >
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-3">
          <Link
            to="/"
            className="flex shrink-0 items-center gap-3 font-bold text-gray-800 hover:opacity-80"
          >
            <img
              src="/images/logo/unilogo.png"
              alt="Integral University Logo"
              className="h-10 w-auto 2xl:h-12"
            />
            <span className="hidden text-xl 2xl:inline">TG-SMART 2026</span>
          </Link>
          <ul className="hidden items-center gap-1 lg:flex 2xl:gap-1">
            {navItems.map((item) => (
              <li key={item.name} className="group relative">
                {item.dropdown ? (
                  <>
                    <button
                      className={`${classes(itemActive(item))} flex items-center gap-0.5`}
                    >
                      {item.name}
                      <ChevronDown size={14} />
                    </button>
                    <div className="invisible absolute left-0 top-full min-w-56 rounded-lg border border-gray-100 bg-white py-2 opacity-0 shadow-xl transition group-hover:visible group-hover:opacity-100">
                      {item.dropdown.map((child) => (
                        <Link
                          key={child.name}
                          to={child.path}
                          className={`block px-4 py-2 text-sm ${active(child.path) ? "bg-primary-600 text-white" : "text-gray-700 hover:bg-blue-50"}`}
                        >
                          {child.name}
                        </Link>
                      ))}
                    </div>
                  </>
                ) : (
                  <Link to={item.path} className={classes(itemActive(item))}>
                    {item.name}
                  </Link>
                )}
              </li>
            ))}
          </ul>
          <button
            onClick={() => setMobileMenuOpen((open) => !open)}
            className="rounded p-2 text-gray-700 hover:bg-blue-100 lg:hidden"
            aria-label="Toggle navigation"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="max-h-[70vh] overflow-y-auto pb-4 lg:hidden">
            <ul className="space-y-1">
              {navItems.map((item) => (
                <li key={item.name}>
                  {item.dropdown ? (
                    <>
                      <p className="rounded bg-blue-50 px-4 py-2 text-sm font-semibold text-primary-700">
                        {item.name}
                      </p>
                      <ul className="ml-4">
                        {item.dropdown.map((child) => (
                          <li key={child.name}>
                            <Link
                              onClick={() => setMobileMenuOpen(false)}
                              to={child.path}
                              className="block rounded px-4 py-2 text-sm text-gray-700 hover:bg-blue-100"
                            >
                              {child.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <Link
                      onClick={() => setMobileMenuOpen(false)}
                      to={item.path}
                      className={classes(itemActive(item)) + " block px-4"}
                    >
                      {item.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </nav>
  );
}
